import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import TextAlign from '@tiptap/extension-text-align'
import CharacterCount from '@tiptap/extension-character-count'
import { useEffect, useState, useRef } from 'react'
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Undo,
  Redo,
  Save,
  CheckCircle,
  XCircle,
  RefreshCw,
  History,
  FileText,
  Clock,
  Loader2,
  ExternalLink,
  Globe,
  Image,
} from 'lucide-react'
import api from '@/services/api'
import toast from 'react-hot-toast'

interface LetterEditorProps {
  letter: {
    id: number
    content: string
    originalContent: string
    status: string
    applicationId: number
    clientId: number
    applicationReference: string | null
    applicationPdfPath: string | null
    clientName: string | null
    territoryName: string | null
    versionCount: number
    createdAt: string
  }
  onUpdate: () => void
  onClose: () => void
}

interface Version {
  id: number
  versionNumber: number
  content: string
  changedByName: string
  changeSummary: string | null
  createdAt: string
}

// Convert plain text to HTML paragraphs
function convertToHtml(text: string): string {
  // If already HTML, return as-is
  if (text.includes('<p>') || text.includes('<div>') || text.includes('<br')) {
    return text
  }
  
  // Split by double newlines for paragraphs, single newlines for line breaks
  const paragraphs = text.split(/\n\n+/)
  return paragraphs
    .map(p => {
      // Convert single newlines to <br> within paragraphs
      const withBreaks = p.trim().replace(/\n/g, '<br>')
      return `<p>${withBreaks}</p>`
    })
    .join('')
}

export default function LetterEditor({ letter, onUpdate, onClose }: LetterEditorProps) {
  const [saving, setSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [hasChanges, setHasChanges] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [clientLogo, setClientLogo] = useState<string | null>(null)
  const [portfolioImages, setPortfolioImages] = useState<(string | null)[]>([null, null, null])
  const [websiteUrl, setWebsiteUrl] = useState<string | null>(null)
  const [versions, setVersions] = useState<Version[]>([])
  const [showOriginal, setShowOriginal] = useState(false)
  const [approving, setApproving] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const autoSaveRef = useRef<NodeJS.Timeout | null>(null)
  const contentRef = useRef(letter.content)

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: 'Start writing your letter...',
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      CharacterCount.configure({
        limit: 10000,
      }),
    ],
    // Convert plain text to HTML paragraphs if needed
    content: convertToHtml(letter.content),
    onUpdate: ({ editor }) => {
      const newContent = editor.getHTML()
      if (newContent !== contentRef.current) {
        setHasChanges(true)
        contentRef.current = newContent
      }
    },
  })

  // Fetch client data (logo, portfolio images, website)
  useEffect(() => {
    const fetchClientData = async () => {
      try {
        const response = await api.get(`/api/v1/clients/${letter.clientId}`)
        if (response.data.logoPath) {
          setClientLogo(response.data.logoPath)
        }
        setPortfolioImages([
          response.data.portfolioImage1,
          response.data.portfolioImage2,
          response.data.portfolioImage3,
        ])
        setWebsiteUrl(response.data.websiteUrl)
      } catch {
        // Client fetch failed
      }
    }
    fetchClientData()
  }, [letter.clientId])

  // Auto-save every 30 seconds
  useEffect(() => {
    if (hasChanges) {
      autoSaveRef.current = setTimeout(() => {
        handleSave(true)
      }, 30000)
    }

    return () => {
      if (autoSaveRef.current) {
        clearTimeout(autoSaveRef.current)
      }
    }
  }, [hasChanges])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault()
        handleSave()
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        handleApprove()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleSave = async (isAutoSave = false) => {
    if (!editor || saving) return

    const content = editor.getHTML()
    if (content === letter.content && !hasChanges) {
      if (!isAutoSave) toast.success('No changes to save')
      return
    }

    setSaving(true)
    try {
      await api.put(`/api/v1/letters/${letter.id}`, {
        content,
        change_summary: isAutoSave ? 'Auto-saved' : 'Manual save',
      })
      setLastSaved(new Date())
      setHasChanges(false)
      contentRef.current = content
      if (!isAutoSave) {
        toast.success('Letter saved')
        onUpdate()
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = async () => {
    if (hasChanges) {
      const confirm = window.confirm('You have unsaved changes. Save before approving?')
      if (confirm) {
        await handleSave()
      }
    }

    setApproving(true)
    try {
      await api.post(`/api/v1/letters/${letter.id}/approve`)
      toast.success('Letter approved')
      onUpdate()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve')
    } finally {
      setApproving(false)
    }
  }

  const handleReject = async () => {
    try {
      await api.post(`/api/v1/letters/${letter.id}/reject`, { reason: 'Rejected from editor' })
      toast.success('Letter rejected')
      onUpdate()
      onClose()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject')
    }
  }

  const handleRegenerate = async () => {
    setRegenerating(true)
    try {
      const response = await api.post(`/api/v1/letters/${letter.id}/regenerate`, {})
      toast.success('Letter regenerated')
      editor?.commands.setContent(convertToHtml(response.data.content))
      contentRef.current = response.data.content
      setHasChanges(false)
      onUpdate()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate')
    } finally {
      setRegenerating(false)
    }
  }

  const fetchHistory = async () => {
    try {
      const response = await api.get(`/api/v1/letters/${letter.id}/history`)
      setVersions(response.data)
      setShowHistory(true)
    } catch (error) {
      toast.error('Failed to load history')
    }
  }

  const restoreVersion = (content: string) => {
    editor?.commands.setContent(content)
    contentRef.current = content
    setHasChanges(true)
    setShowHistory(false)
    toast.success('Version restored - remember to save')
  }

  const wordCount = editor?.storage.characterCount.words() || 0
  const charCount = editor?.storage.characterCount.characters() || 0

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden border border-slate-700">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700 flex items-center justify-between bg-slate-800/50">
          <div>
            <h2 className="text-lg font-semibold text-slate-100">Edit Letter</h2>
            <div className="flex items-center gap-4 mt-1 text-sm text-slate-400">
              <span>{letter.clientName}</span>
              <span>•</span>
              <span>{letter.applicationReference}</span>
              <span>•</span>
              <span>{letter.territoryName}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Auto-save indicator */}
            {lastSaved && (
              <span className="text-xs text-slate-500 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Saved {lastSaved.toLocaleTimeString()}
              </span>
            )}
            {hasChanges && (
              <span className="text-xs text-amber-400 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-amber-400" />
                Unsaved changes
              </span>
            )}
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-200 p-2"
            >
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-2 border-b border-slate-700 flex items-center justify-between bg-slate-800/30">
          <div className="flex items-center gap-1">
            {/* Text formatting */}
            <button
              onClick={() => editor?.chain().focus().toggleBold().run()}
              className={`p-2 rounded hover:bg-slate-700 ${editor?.isActive('bold') ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Bold (Ctrl+B)"
            >
              <Bold className="w-4 h-4" />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleItalic().run()}
              className={`p-2 rounded hover:bg-slate-700 ${editor?.isActive('italic') ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Italic (Ctrl+I)"
            >
              <Italic className="w-4 h-4" />
            </button>
            <button
              onClick={() => editor?.chain().focus().toggleUnderline().run()}
              className={`p-2 rounded hover:bg-slate-700 ${editor?.isActive('underline') ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Underline (Ctrl+U)"
            >
              <UnderlineIcon className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-slate-700 mx-2" />

            {/* Alignment */}
            <button
              onClick={() => editor?.chain().focus().setTextAlign('left').run()}
              className={`p-2 rounded hover:bg-slate-700 ${editor?.isActive({ textAlign: 'left' }) ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Align left"
            >
              <AlignLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => editor?.chain().focus().setTextAlign('center').run()}
              className={`p-2 rounded hover:bg-slate-700 ${editor?.isActive({ textAlign: 'center' }) ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Align center"
            >
              <AlignCenter className="w-4 h-4" />
            </button>
            <button
              onClick={() => editor?.chain().focus().setTextAlign('right').run()}
              className={`p-2 rounded hover:bg-slate-700 ${editor?.isActive({ textAlign: 'right' }) ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Align right"
            >
              <AlignRight className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-slate-700 mx-2" />

            {/* Undo/Redo */}
            <button
              onClick={() => editor?.chain().focus().undo().run()}
              disabled={!editor?.can().undo()}
              className="p-2 rounded hover:bg-slate-700 text-slate-400 disabled:opacity-30"
              title="Undo (Ctrl+Z)"
            >
              <Undo className="w-4 h-4" />
            </button>
            <button
              onClick={() => editor?.chain().focus().redo().run()}
              disabled={!editor?.can().redo()}
              className="p-2 rounded hover:bg-slate-700 text-slate-400 disabled:opacity-30"
              title="Redo (Ctrl+Shift+Z)"
            >
              <Redo className="w-4 h-4" />
            </button>

            <div className="w-px h-6 bg-slate-700 mx-2" />

            {/* History & Original */}
            <button
              onClick={fetchHistory}
              className="p-2 rounded hover:bg-slate-700 text-slate-400 flex items-center gap-1"
              title="Version history"
            >
              <History className="w-4 h-4" />
              <span className="text-xs">{letter.versionCount}</span>
            </button>
            <button
              onClick={() => setShowOriginal(!showOriginal)}
              className={`p-2 rounded hover:bg-slate-700 ${showOriginal ? 'bg-slate-700 text-white' : 'text-slate-400'}`}
              title="Show original AI version"
            >
              <FileText className="w-4 h-4" />
            </button>

            {letter.applicationPdfPath && (
              <>
                <div className="w-px h-6 bg-slate-700 mx-2" />
                <button
                  onClick={async () => {
                    try {
                      // SEC-001: fetch via authed endpoint (was /planning_downloads/... public mount)
                      const resp = await api.get(
                        `/api/v1/applications/${letter.applicationId}/source-pdf`,
                        { responseType: 'blob' },
                      )
                      const url = URL.createObjectURL(resp.data)
                      window.open(url, '_blank')
                    } catch (err: any) {
                      toast.error(err.response?.data?.detail || 'Failed to load source PDF')
                    }
                  }}
                  className="px-3 py-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white flex items-center gap-2 text-sm"
                  title="Open source PDF application"
                >
                  <ExternalLink className="w-4 h-4" />
                  <span>Open Source PDF</span>
                </button>
              </>
            )}
          </div>

          {/* Word count */}
          <div className="text-xs text-slate-500">
            {wordCount} words • {charCount} characters
          </div>
        </div>

        {/* Editor Content */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Regeneration Overlay */}
          {regenerating && (
            <div className="absolute inset-0 bg-slate-900/80 z-10 flex flex-col items-center justify-center">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-slate-200 text-lg font-medium">Regenerating letter...</p>
              <p className="text-slate-400 text-sm mt-2">This may take a few seconds</p>
              <div className="mt-4 w-64 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div className="h-full bg-blue-500 rounded-full animate-pulse" style={{ width: '100%' }} />
              </div>
            </div>
          )}
          
          {/* Main Editor */}
          <div className={`flex-1 overflow-auto p-6 bg-slate-700/30 ${showOriginal || showHistory ? 'w-1/2' : 'w-full'}`}>
            {/* A4 size: 210mm x 297mm = 794px x 1123px at 96dpi */}
            <div className="bg-white rounded-lg shadow-lg mx-auto" style={{ width: '794px', minHeight: '1123px' }}>
              {/* Letter Header with Logo */}
              <div className="pt-10 px-16 pb-4">
                <div className="flex justify-end">
                  {clientLogo ? (
                    <img
                      src={clientLogo.startsWith('/') ? clientLogo : `/${clientLogo}`}
                      alt={letter.clientName || 'Client logo'}
                      className="max-h-24 max-w-[280px] object-contain"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none'
                      }}
                    />
                  ) : (
                    <div className="text-right text-slate-400 text-sm italic">
                      No logo uploaded
                    </div>
                  )}
                </div>
              </div>
              {/* Editor Area */}
              <div className="px-16 pb-8">
                <EditorContent
                  editor={editor}
                  className="letter-editor min-h-[700px]"
                />
              </div>
              
              {/* Portfolio Images & Footer Preview */}
              {(portfolioImages.some(img => img) || websiteUrl) && (
                <div className="px-16 pb-10">
                  {/* Separator line */}
                  <div className="border-t border-gray-300 mb-4"></div>
                  
                  {/* Portfolio Images */}
                  {portfolioImages.some(img => img) && (
                    <div className="mb-4">
                      <div className="flex gap-4 justify-center">
                        {portfolioImages.map((img, idx) => (
                          img ? (
                            <div key={idx} className="w-1/3 max-w-[180px]">
                              <img
                                src={img.startsWith('/') ? img : `/${img}`}
                                alt={`Portfolio ${idx + 1}`}
                                className="w-full h-28 object-cover rounded shadow-sm"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                }}
                              />
                            </div>
                          ) : null
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Website Footer */}
                  {websiteUrl && (
                    <>
                      <div className="border-t border-gray-300 mb-3"></div>
                      <div className="text-center text-gray-500 text-sm flex items-center justify-center gap-2">
                        <Globe className="w-4 h-4" />
                        {websiteUrl}
                      </div>
                    </>
                  )}
                </div>
              )}
              
              {/* No portfolio/website message */}
              {!portfolioImages.some(img => img) && !websiteUrl && (
                <div className="px-16 pb-10">
                  <div className="border-t border-dashed border-gray-300 pt-4">
                    <div className="text-center text-gray-400 text-xs flex items-center justify-center gap-2">
                      <Image className="w-4 h-4" />
                      <span>Portfolio images and website footer will appear here when configured in Client settings</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Side Panel - Original or History */}
          {(showOriginal || showHistory) && (
            <div className="w-1/2 border-l border-slate-700 overflow-auto bg-slate-800/30">
              {showOriginal && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-slate-200">Original AI Version</h3>
                    <button
                      onClick={() => setShowOriginal(false)}
                      className="text-slate-400 hover:text-slate-200"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="bg-slate-900 rounded-lg p-6 text-slate-300 font-serif text-sm leading-relaxed whitespace-pre-wrap">
                    {letter.originalContent}
                  </div>
                </div>
              )}

              {showHistory && (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-slate-200">Version History</h3>
                    <button
                      onClick={() => setShowHistory(false)}
                      className="text-slate-400 hover:text-slate-200"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                  {versions.length === 0 ? (
                    <p className="text-slate-400 text-sm">No previous versions</p>
                  ) : (
                    <div className="space-y-4">
                      {versions.map((version) => (
                        <div
                          key={version.id}
                          className="bg-slate-900 rounded-lg p-4 border border-slate-700"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-200">
                              Version {version.versionNumber}
                            </span>
                            <button
                              onClick={() => restoreVersion(version.content)}
                              className="text-xs text-blue-400 hover:text-blue-300"
                            >
                              Restore
                            </button>
                          </div>
                          <p className="text-xs text-slate-400 mb-2">
                            {version.changedByName} • {new Date(version.createdAt).toLocaleString()}
                          </p>
                          {version.changeSummary && (
                            <p className="text-xs text-slate-500 mb-2">{version.changeSummary}</p>
                          )}
                          <div className="text-xs text-slate-400 line-clamp-3 font-serif">
                            {version.content.replace(/<[^>]*>/g, '').substring(0, 200)}...
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-slate-700 flex items-center justify-between bg-slate-800/50">
          <div className="flex items-center gap-2">
            <button
              onClick={handleRegenerate}
              disabled={regenerating}
              className="btn-secondary flex items-center gap-2"
            >
              {regenerating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Regenerate
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => handleSave()}
              disabled={saving || !hasChanges}
              className="btn-secondary flex items-center gap-2"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save Draft
            </button>

            {letter.status !== 'approved' && (
              <>
                <button
                  onClick={handleReject}
                  className="btn-secondary flex items-center gap-2 text-red-400 hover:text-red-300"
                >
                  <XCircle className="w-4 h-4" />
                  Reject
                </button>
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="btn-primary flex items-center gap-2"
                >
                  {approving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Approve
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
